import React, { useState, useContext, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";

import { dataType } from "../../../components/GenericList/GenericList";
import Select from "@mui/material/Select";
import { theme } from "../../../theme";
import FdTypography from "../../../components/UIComponents/Typography/FdTypography";
import FdButton from "../../../components/UIComponents/Button/FdButton";
import { Autocomplete, TextField } from "@mui/material";

import { TbCirclePlus } from "react-icons/tb";
// import { setOriginalNode } from "typescript";
import FdIconButton from "../../../components/UIComponents/IconButton/FdIconButton";
import { BiTrash } from "react-icons/bi";
import { APIContext } from "../../../store/APIStore";
import NotificationMessage, {
  notificationType,
} from "../../../components/NotificationMessage/NotificationMessage";
import { IFieldsTemplate, attendeeFieldsTemplate, getFieldLabel, reservationFieldsTemplate, sessionFieldsTemplate, attendeeAddonCustomFields, IGetCustomFields, attendeeDefaultFields } from "../../../helpers/constants";

const classes = {
  baseWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "calc(100vh - 150px)",
  },
  fieldsWrapper: {
    py: "15px",
    pl: "15px",
    pr: 0,
    width: "95%",
    overflow: 'auto',
    m: "auto",
    flexGrow: 1,
    border: `1px solid ${theme.palette.grey[600]}`,
  },
  backButton: {
    fontSize: "12px",
    textDecoration: "underline",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "none",
    },
  },
} as const;

export interface IProps {
  initialMap?: any;
  csvType: dataType;
  csvData: any[]; // the imported csv data array
  onFormSubmit: Function;
  onCancel: Function;
}

type rowData = {
  [index: string]: string;
};

const ERROR_EMPTY_FIELD = "Please enter field name";
const ERROR_DUPLICATE_FIELD = "This field already exists, try a different name or use the existing one.";
const SKIP_DEFAULT_FIELDS = [
  "fname", "lname", "company", "country", "email", "jobtitle", "clientid",
  "createdonsite", // PROD env has this fieldname in lower case
  "createdOnsite", // NON PROD env has this fieldname in camel case
];

interface IFieldTemplates {
  [index: string]: IFieldsTemplate;
}

interface ICustomField {
  isEnable: boolean;
  label: string;
  key: string;
  ERROR: string;
  idCustom: number;
};

const initCustomField: ICustomField = {
  isEnable: false,
  label: "",
  key: "",
  ERROR: "",
  idCustom: -1,
};

const fieldTemplates: IFieldTemplates = {
  [dataType.ATTENDEES]: attendeeFieldsTemplate,
  [dataType.SESSIONS]: sessionFieldsTemplate,
  [dataType.RESERVATIONS]: reservationFieldsTemplate,
};

function CSVConfigForm({
  csvType,
  csvData,
  onCancel,
  onFormSubmit,
  initialMap,
}: IProps) {
  const notificationRef = useRef<any>();
  const inputRef = useRef<any>();
  const api = useContext(APIContext);
  const { eventId } = useParams();
  const [addCustomField, setCustomField] = useState<ICustomField>(initCustomField);
  const [customOptions, setCustomOptions] = useState<Array<IGetCustomFields>>([]);
  const [addonList, setAddonList] = useState<IFieldsTemplate>({});
  const [loadingAddCustom, setLoadingAddCustom] = useState<boolean>(false);

  const templateFields = fieldTemplates[csvType];
  const isEnableCustomField = csvType === dataType.ATTENDEES;

  const fetchAttendeeFields = async () => {
    try {
      const data = await api.eventService.getAttendeeFields(Number(eventId));
      if (data && Array.isArray(data)) {
        const filterList: Array<IGetCustomFields> = data.filter(item => item.label && !item?.deleted && item.visible && !SKIP_DEFAULT_FIELDS.includes(item.fieldName)).map((each) => {
          return {
            id: each.id,
            label: each.label,
            fieldName: each.fieldName,
            visible: each.visible,
            deleted: each.deleted,
            isApplied: true,
          }
        });
        let currentList: IFieldsTemplate = {};
        attendeeDefaultFields.forEach((each) => {
          if (each?.label && each?.fieldName) {
            currentList[each.fieldName] = each.label;
          }
        });
        if (filterList && filterList.length) {
          filterList.forEach((each) => {
            if (each?.label && each?.fieldName) {
              currentList[each.fieldName] = each.label;
            }
          });
        }
        setAddonList(currentList);
        setCustomOptions([...attendeeDefaultFields, ...filterList]);
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  useEffect(() => {
    if (isEnableCustomField) {
      fetchAttendeeFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCustomField = () => {
    const objDiv = document.getElementById("sub_main_div");
    if (objDiv !== null) {
      setTimeout(() => {
        objDiv.scrollTo(0, objDiv.scrollHeight + 30);
        inputRef?.current?.focus();
      }, 100);
    }
    setCustomField({ ...initCustomField, isEnable: true });
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomField((prevState) => ({
      ...prevState,
      ERROR: "",
      label: String(e.target.value)?.trim(),
      key: "",
      idCustom: -1,
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = String(e.target.value).trim().replace(" ", "").toUpperCase();
    const item = customOptions.find((each) => Boolean(each?.label && value && String(each?.label).trim().replace(" ", "").toUpperCase() === value));

    if (item) {
      setCustomField((prevState) => ({
        ...prevState,
        label: String(item?.label),
        idCustom: Number(item?.id),
        key: String(item?.fieldName),
      }));
    }
  };

  const onAddCustomField = async (payload?: ICustomField) => {
    const current = payload?.label ? payload : addCustomField;
    // const insertKey = current?.key ? current.key : String(current?.label)?.replace(' ', '_').toUpperCase();
    const insertValue = String(current?.label)?.charAt(0).toUpperCase() + String(current?.label)?.slice(1);
    if (insertValue) {
      const idCustom = current?.idCustom || -1;
      if (idCustom > -1) {
        const isAlreadyApplied = customOptions.findIndex((each) => Number(each.id) === Number(idCustom) && each.isApplied);
        let fetchField: IGetCustomFields | undefined = undefined;
        if (isAlreadyApplied > -1) {
          fetchField = customOptions[isAlreadyApplied];
          notificationRef.current?.show(
            ERROR_DUPLICATE_FIELD,
            notificationType.Error
          );
        }
        setCustomField(initCustomField);
        setCustomOptions((prevState) => {
          return prevState.map((each) => {
            if (Number(each.id) === Number(idCustom)) return { ...each, isApplied: true };
            return each;
          })
        });
        if (fetchField && fetchField?.fieldName) {
          setAddonList(prevState => {
            return {
              ...prevState,
              [fetchField?.fieldName || insertValue]: insertValue
            }
          });
        } else {
          setAddonList(prevState => {
            return {
              ...prevState,
              [insertValue]: insertValue
            }
          });
        }
      } else {
        // API
        try {
          setLoadingAddCustom(true);
          // call the api
          const data = await api.attendeeFieldService.createAttendeeField(
            Number(eventId),
            {
              fieldName: insertValue,
              label: insertValue,
              required: false,
              visible: true,
              dataType: "TEXT_ONE_LINE_FIELD",
            }
          ) as unknown as IGetCustomFields;
          if (data) {
            setLoadingAddCustom(false);
            setCustomField(initCustomField);
            setCustomOptions(prevState => {
              return [
                ...prevState,
                {
                  id: data?.id,
                  deleted: false,
                  visible: true,
                  isApplied: true,
                  label: data?.label,
                  fieldName: data?.fieldName,
                }
              ];
            });
            setAddonList(prevState => {
              return {
                ...prevState,
                [data?.fieldName || insertValue]: insertValue
              }
            });
          }
        } catch (error: any) {
          setLoadingAddCustom(false);
          setCustomField(initCustomField);
          console.log(error.response);
          let errorMessage = error?.response?.data?.message || "";
          if (errorMessage?.includes("Duplicate")) errorMessage = ERROR_DUPLICATE_FIELD;
          notificationRef.current?.show(
            errorMessage,
            notificationType.Error
          );
        }
      }
    } else {
      setCustomField((prevState) => ({
        ...prevState,
        ERROR: ERROR_EMPTY_FIELD,
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // @ts-ignore
      const value = String(e.target.value).trim().replace(" ", "").toUpperCase();
      const item = customOptions.find((each) => Boolean(each?.label && value && String(each?.label).trim().replace(" ", "").toUpperCase() === value));
      if (item) {
        onAddCustomField({
          ...addCustomField,
          label: String(item?.label),
          idCustom: Number(item?.id),
          key: String(item?.fieldName),
        });
      } else onAddCustomField();
    }
  }

  const onDeleteField = (pos: number) => {
    if (pos > -1) {
      const deleteKey = Object.keys(addonList)[pos];
      const deleteItem = String(addonList[deleteKey]).trim().replace(" ", "").toUpperCase();
      setCustomOptions((prevState) => {
        return prevState.map((each) => {
          if (String(each.label).trim().replace(" ", "").toUpperCase() === deleteItem) return { ...each, isApplied: false };
          return each;
        });
      });
      //@ts-ignore
      unregister(addonList[deleteKey])
      delete addonList[deleteKey];
      setAddonList({ ...addonList });
    }
  };

  const handleOptionChange = (e: React.ChangeEvent<HTMLSelectElement>, pos: number) => {
    if (pos > -1) {
      const current = String(e.target.value).trim();

      const deleteKey = Object.keys(addonList)[pos];
      const deleteItem = String(addonList[deleteKey]).trim().replace(" ", "").toUpperCase();

      const insertKey = String(current)?.replace(' ', '_').toUpperCase();
      const insertValue = String(current)?.charAt(0).toUpperCase() + String(current)?.slice(1);

      setCustomOptions((prevState) => {
        return prevState.map((each) => {
          if (String(each.label).trim().replace(" ", "").toUpperCase() === deleteItem) return { ...each, isApplied: false };
          if (String(each.label).trim().replace(" ", "").toUpperCase() === current.replace(" ", "").toUpperCase()) return { ...each, isApplied: true };
          return each;
        });
      });

      delete addonList[deleteKey];
      setAddonList({ ...addonList, [insertKey]: insertValue });

    }
  };

  const onSelectCustomField = (item: IGetCustomFields) => {
    setCustomField((prevState) => ({
      ...prevState,
      label: String(item.label),
      idCustom: Number(item.id),
      key: String(item.fieldName),
    }));
  };

  const getCSVColumns = () => {
    return Object.keys(csvData[0]).map((key) => {
      return (
        <MenuItem key={key} value={key}>
          {key}
        </MenuItem>
      );
    });
  };

  const getCSVOptions = () => {
    return customOptions.map((each) => {
      return (
        <MenuItem
          key={each.id}
          value={each.label}
          disabled={Boolean(each.isApplied)}
        >
          {each.label}
        </MenuItem>
      );
    });
  };

  const isValidation = (current: any) => {
    for (const field of Object.values(templateFields)) {
      if (!current[field]) {
        return { [field]: "" };
      }
    }

    return;
  }

  // try to prefill the mapping form, use the already selected map or try to use the csv columns
  const getInitialValues = () => {
    const initialValues: rowData = {};
    if (initialMap) {
      return initialMap;
    }

    const currentValues = isEnableCustomField ? { ...templateFields, ...attendeeAddonCustomFields } : templateFields;
    Object.values({ ...currentValues }).forEach((field, index) => {
      initialValues[field] = Object.keys(csvData[0])[index] || "";
    });

    return initialValues;
  };

  const csvColumns = getCSVColumns();
  const csvOption = getCSVOptions();
  const initialValues = getInitialValues();
  const {
    handleSubmit,
    unregister,
    control,
    watch,
  } = useForm<FormData>({
    defaultValues: initialValues,
  });

  const onSubmit = async (values: any) => {
    const hasError = isValidation(values);
    if (!hasError) onFormSubmit(values);
  };

  return (
    <Box sx={classes.baseWrapper}>
      <Box>
        <FdTypography
          label="Map imported CSV fields"
          variant="h2"
          component="h2"
          mb="10px"
        />
        <FdTypography
          label="Map imported CSV fields with the existing fielddrive infofields or create a new infofield  "
          variant="body1"
          component="h6"
          mb="10px"
        />
      </Box>

      <Box sx={classes.fieldsWrapper} id="main_div">
        <Box
          sx={{
            height: "100%",
            overflow: "auto",
            paddingRight: "15px",
          }}
          id="sub_main_div"
        >
          <form onSubmit={handleSubmit((values) => onSubmit(values))} id="configForm" noValidate>
            {Object.values(templateFields).map((field) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "15px",
                  gap: 2,
                  // @ts-ignore
                  backgroundColor: watch(field) ? '#F2F8EA' : '#FDF0EE',
                  padding: "15px",
                  borderRadius: "5px",
                  "& .MuiBox-root": { width: "100%" },
                  "& .MuiFormControl-root": { m: 0 },
                }}
              >
                <Box>
                  <TextField
                    disabled
                    sx={{ "&.MuiFormControl-root": { "&:hover": { cursor: "not-allowed" } }, "& .MuiInputBase-root": { background: "#F0F0F0", color: '#C0C0C0', }, "& .MuiFormHelperText-root": { my: '8px', mx: 0 } }}
                    placeholder={field}
                    value={getFieldLabel(field)}
                    // @ts-ignore
                    error={Boolean(!watch(field))}
                    // @ts-ignore
                    helperText={watch(field) ? "" : "This field is mandatory to be mapped."}
                  />
                </Box>
                <Box key={field}>
                  <Controller
                    //@ts-ignore
                    name={field}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        required
                        fullWidth
                        sx={{ textAlign: "left" }}
                        displayEmpty={true}
                        defaultValue={"" as unknown}
                      >
                        <MenuItem key={"DEFAULT"} value={""}>
                          <em>Select CSV field</em>
                        </MenuItem>
                        {csvColumns}
                      </Select>
                    )}
                  />
                </Box>
              </Box>
            ))}

            {Object.values(addonList).map((field, pos) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "15px",
                  gap: 2,
                  backgroundColor: theme.palette.grey[600],
                  padding: "15px",
                  borderRadius: "5px",
                  "& .MuiBox-root": { width: "100%", transition: 'all 1s ease-out', },
                  "& .MuiFormControl-root": { m: 0 },
                  "& .deleteBtn": {
                    transition: 'opacity 1s ease-out',
                    opacity: 0,
                    width: 0,
                    overflow: 'hidden',
                  },
                  "&:hover": {
                    "& .deleteBtn": {
                      opacity: 1,
                      width: 'initial', ml: 1,
                    }
                  },
                }}
              >
                <Box>
                  <Controller
                    //@ts-ignore
                    name={field}
                    control={control}
                    render={({ field }) => {
                      return (<Select
                        {...field}
                        required
                        value={field.name}
                        onChange={(e: any) => handleOptionChange(e, pos)}
                        fullWidth
                        sx={{ textAlign: "left", '& em': { color: '#C0C0C0' } }}

                      >
                        {csvOption}
                      </Select>)
                    }}
                  />
                </Box>
                <Box key={field} sx={{ display: "flex", alignItems: "center" }}>
                  <Controller
                    //@ts-ignore
                    name={field}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        required
                        fullWidth
                        sx={{ textAlign: "left", '& em': { color: '#C0C0C0' } }}
                        displayEmpty={true}
                        defaultValue={"" as unknown}
                      >
                        <MenuItem value=''><em>Select CSV field</em>
                        </MenuItem>
                        {csvColumns}
                      </Select>
                    )}
                  />
                  <Box className="deleteBtn">
                    <FdIconButton onClick={() => onDeleteField(pos)} Icon={<BiTrash />} />
                  </Box>
                </Box>

              </Box>
            ))}

            {isEnableCustomField && addCustomField.isEnable ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "15px",
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Controller
                    //@ts-ignore
                    name="customAutocomplete"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        forcePopupIcon
                        freeSolo
                        disableClearable
                        options={customOptions.filter(each => !Boolean(each?.isApplied))}
                        onChange={(e, value) => { e.preventDefault(); onSelectCustomField(value as IGetCustomFields); }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            {...field}
                            inputRef={input => { inputRef.current = input }}
                            value={addCustomField.label}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            error={Boolean(addCustomField?.ERROR)}
                            helperText={addCustomField?.ERROR}
                            placeholder="Select or add new infofield"
                          />
                        )}
                      />
                    )}
                  />
                </Box>


                <Box sx={{ display: "flex", gap: 1 }}>
                  <FdButton variant="text" ButtonName="Cancel" onClick={() => setCustomField(initCustomField)} />
                  <FdButton size="small" isLoading={loadingAddCustom} disabled={loadingAddCustom || Boolean(addCustomField?.ERROR)} ButtonName="Save" onClick={() => onAddCustomField()} />
                </Box>
              </Box>
            ) : (
              <></>
            )}

            {isEnableCustomField && !Boolean(addCustomField?.isEnable || false) ? (
              <Box textAlign="center">
                <FdButton
                  variant="text"
                  color="primary"
                  onClick={handleCustomField}
                  startIcon={<TbCirclePlus />}
                  ButtonName="Add infofield"
                />
              </Box>
            ) : (
              ""
            )}

          </form>
        </Box>
      </Box>

      <Box textAlign="center" marginTop="10px" sx={{ display: "flex", gap: 1 }}>

        <FdButton variant="text" color="primary" ButtonName="Cancel" onClick={() => onCancel()} />
        <FdButton
          variant="contained"
          color="primary"
          ButtonName="Save"
          type="submit"
          form="configForm"
        />
      </Box>
      <NotificationMessage ref={notificationRef} />
    </Box>

  );
}

export default CSVConfigForm;
